import { loadConfig } from './config.js';
import { Bus, wsUrl } from './bus.js';
import { playDelayed, hlsUrl, type DelayedPlayer } from './delayed.js';
import { publish, whipUrl } from './whip.js';
import { VitalsCollector } from './vitals.js';
import { syntheticSource, type SyntheticSource } from './synthetic.js';
import { mountNav } from './nav.js';
import {
  closesDialog,
  holdLabel,
  type MediaHealth,
  mediaToken,
  publisherView,
  type Screen,
  type Stage,
  startFailure,
  trapIndex,
  viewerScreen,
  viewerStage,
  withToken,
} from './view.js';
import {
  publishState, mainView, broadcastMuted, soundBlockedReason, stopNeedsConfirm,
  primaryAction, tileRelation, publisherHeadline, type PublishState, type MainView,
} from './stage.js';
import {
  cameraConstraints, chooseCamera, cameraName, activeCameraLabel, prefersRearCamera,
  type VideoInput,
} from './devices.js';
import { slateCopy, slateActivity } from '../shared/slate-copy.js';
import { standardLink, appealLine } from './view.js';
import { eligibility } from '../shared/scoring.js';
import { DEFAULT_STANDARD, type Feed, type FeedView, type OnAir, type StandardConfig,
  locationLabel, effectiveLocation, type Location,
} from '../shared/types.js';
import { channelLines, fateContext, type Feedback } from '../shared/feedback.js';

/**
 * The one public page: the broadcast, and your own camera beside it.
 *
 * Everything with a decision in it lives in `stage.ts`, `view.ts` or
 * `devices.ts`, all pure and all unit tested. What is left here is DOM wiring
 * and media plumbing, which is the split issue 010 asks for: seven of the
 * eleven defects two reviews found in `src/client/` were decisions buried in
 * handlers, and none of the seven was found by a test.
 */

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const air = $<HTMLVideoElement>('air');
const me = $<HTMLVideoElement>('me');
const tile = $<HTMLDivElement>('tile');
const swapBtn = $<HTMLButtonElement>('swap');
const pill = $<HTMLDivElement>('pill');
const creditLine = $<HTMLDivElement>('credit-line');
const tileRelationEl = $<HTMLDivElement>('tile-relation');
const slateMark = $<HTMLDivElement>('slate-mark');
const slateReason = $<HTMLDivElement>('slate-reason');
const slateClause = $<HTMLDivElement>('slate-clause');
const darkMark = $<HTMLDivElement>('dark-mark');
const goodBtn = $<HTMLButtonElement>('rate-good');
const badBtn = $<HTMLButtonElement>('rate-bad');
const soundBtn = $<HTMLButtonElement>('sound');
const primaryBtn = $<HTMLButtonElement>('primary');
const noteEl = $<HTMLDivElement>('note');
const whyBtn = $<HTMLButtonElement>('why');
const sheet = $<HTMLElement>('sheet');
const sheetClose = $<HTMLButtonElement>('sheet-close');
const sheetBroadcast = $<HTMLDivElement>('sheet-broadcast');
const sheetLocation = $<HTMLDivElement>('sheet-location');
const locationEl = $<HTMLInputElement>('location');
// The angle, live. `change` rather than `input`, so a rename lands when the
// publisher has finished typing rather than on every keystroke.

const locationInherited = $<HTMLDivElement>('location-inherited');

/**
 * The two fields behave DIFFERENTLY once publishing, and one function decides
 * it so they cannot drift apart.
 *
 * The angle stays editable and goes live on its own frame: it is free text so
 * viewers can tell nine streams of one court apart, and a person moves seats.
 *
 * The location LOCKS. It is written into every archive row, so changing it
 * mid-broadcast would make rows before and after mean different things about
 * one stream. Disabled rather than silently ignored, which is what it was: the
 * value was read only in the `register` frame, so typing here after going live
 * changed nothing and said nothing.
 */

let angleWired = false;
/** Wired once, on the first publish, so it is unambiguously after every declaration. */
function wireAngleOnce(): void {
  if (angleWired) return;
  angleWired = true;
  angleInput.addEventListener('change', () => {
    // Only while live. Before registering, the value rides on the `register`
  // frame and sending it early would name a feed that does not exist yet.
    if (!publishing) return;
    const angle = angleInput.value.trim();
    if (angle === '') return;
    bus?.send({ type: 'angle', angle });
    localStorage.setItem('sb.angle', angle);
  });
}

function applyFieldLocks(): void {
  locationEl.disabled = publishing;
  locationInherited.textContent = publishing
    ? 'Location is fixed while you are live. Stop, change it, and start again.'
    : inheritedNote();
  locationInherited.hidden = locationInherited.textContent === '';
}

/** What a publisher inherits if they leave the field blank. */
function inheritedNote(): string {
  const inherited = locationLabel(sessionLocation);
  return inherited === null ? '' : `Streaming from ${inherited} unless you say otherwise.`;
}
const sheetMine = $<HTMLDivElement>('sheet-mine');
const cameraSelect = $<HTMLSelectElement>('camera');
const cameraActiveEl = $<HTMLDivElement>('camera-active');
const operatorInput = $<HTMLInputElement>('operator');
const angleInput = $<HTMLInputElement>('angle');
const syntheticBox = $<HTMLInputElement>('synthetic');
const verdictEl = $<HTMLDivElement>('verdict');
const reasonsEl = $<HTMLUListElement>('reasons');
const feedbackEl = $<HTMLElement>('feedback');
const rulingEl = $<HTMLDivElement>('fb-ruling');
const fateEl = $<HTMLDivElement>('fb-fate');
const coachingEl = $<HTMLDivElement>('fb-coaching');
const statusEl = $<HTMLDivElement>('status');

// --- watching -----------------------------------------------------------------
let hlsBase = '';
let mediaBase = '';
/** Where a human reads the rule that held a feed. Filled from `/api/config`. */
let standardUrl = '';
let holdSeconds: number | null = null;
let currentPath: string | null = null;
let player: DelayedPlayer | null = null;
let media: MediaHealth | null = null;
let liveFeedId: string | null = null;
let feeds: readonly FeedView[] = [];
/** The session's DEFAULT location. A feed's own, when set, is on the feed. */
let sessionLocation: Location | null = null;
let onAir: OnAir = { kind: 'slate', since: 0, reason: 'connecting' };
let standard: StandardConfig = DEFAULT_STANDARD;
let connected = true;
let wantsSound = false;
const FIRST_FRAME_GRACE_MS = 6_000;

// --- publishing ---------------------------------------------------------------
let publishing = false;
let myFeedId: string | null = null;
let myFeed: Feed | null = null;
let wasOnAir = false;
let pinned: MainView | null = null;
let cameras: VideoInput[] = [];
let chosenCamera: string | null = localStorage.getItem('sb.camera');
let sender: RTCRtpSender | null = null;
let liveStream: MediaStream | null = null;
let synthetic: SyntheticSource | null = null;
let bus: Bus | null = null;
let myPath = '';
let pc: RTCPeerConnection | null = null;
let collector: VitalsCollector | null = null;
let vitalsTimer = 0;

const state = (): PublishState => publishState(publishing, myFeedId !== null && onAirId() === myFeedId, wasOnAir);
const onAirId = (): string | null => (onAir.kind === 'feed' ? onAir.feedId : null);

// --- the broadcast tile -------------------------------------------------------

function showFeed(path: string, credit: string): void {
  if (holdSeconds === null) return;
  currentPath = path;
  player?.destroy();
  media = { hasFrames: false, attachedAt: Date.now(), fatal: false };
  player = playDelayed(hlsUrl(hlsBase, path), air, holdSeconds, (h) => {
    if (media === null) return;
    media = { ...media, hasFrames: h.hasFrames || media.hasFrames, fatal: h.fatal };
    paint();
  });
  creditLine.textContent = credit;
  paint();
}

function showSlate(reason: string): void {
  media = null;
  if (currentPath !== null) {
    player?.destroy();
    player = null;
    currentPath = null;
  }
  const copy = slateCopy(reason);
  slateMark.textContent = copy.mark;
  /*
   * P2.12's remainder: name the clause where a viewer may be told, and say how
   * many were withheld where they may not.
   *
   * Read off `onAir` rather than derived here, because `toSlate` is the only
   * writer and it is already filtered: deriving it a second time in a browser
   * would be a second copy of the decision, in the one place that cannot be
   * trusted. `textContent`, never markup, so a clause id is text by
   * construction.
   */
  const held = onAir.kind === 'slate' ? onAir : null;
  const parts: string[] = [];
  if (held?.clause !== undefined) parts.push(`Held under ${held.clause}.`);
  if (held?.withheld !== undefined && held.withheld > 0) {
    parts.push(held.withheld === 1
      ? 'One more rule fired that cannot be named here, because naming it would be the '
        + 'disclosure it exists to prevent.'
      : `${String(held.withheld)} more rules fired that cannot be named here, because naming `
        + 'them would be the disclosure they exist to prevent.');
  }
  slateClause.textContent = parts.join(' ');
  // What is happening behind the slate, when anything is. Nobody streaming and
  // three streams failing to pass are different facts, and this rendered them
  // the same: the count was on the state frame the whole time and never shown.
  const activity = slateActivity(feeds.length, false);
  slateReason.textContent = activity ? `${copy.why} ${activity}` : copy.why;
  creditLine.textContent = '';
  paint();
}

function applyState(): void {
  const stage = viewerStage(feeds, onAir, currentPath, holdSeconds);
  if (stage.kind === 'wait') return;
  if (stage.kind === 'slate') {
    liveFeedId = null;
    showSlate(stage.reason);
    return;
  }
  const id = onAirId();
  if (id !== liveFeedId) {
    liveFeedId = id;
    goodBtn.classList.remove('chosen');
    badBtn.classList.remove('chosen');
  }
  if (stage.changed) showFeed(stage.path, stage.credit);
}

// --- one painter --------------------------------------------------------------

function paint(): void {
  const s = state();
  const stage: Stage =
    currentPath === null
      ? { kind: 'slate', reason: '' }
      : { kind: 'feed', path: currentPath, credit: creditLine.textContent ?? '', changed: false };
  const screen: Screen = viewerScreen(stage, media, Date.now(), FIRST_FRAME_GRACE_MS);

  document.body.dataset.state = screen;
  document.body.dataset.publish = s;
  document.body.dataset.main = mainView(s, pinned);

  darkMark.textContent = screen === 'connecting' ? 'CONNECTING' : 'NOT REACHING THIS DEVICE';
  const hold = holdLabel(screen, holdSeconds, player?.latency() ?? null);
  /*
    * "SLATE" was the deepest jargon on this page: this project's own word for
    * its own product, meaning nothing at all to the person reading it. The
    * concept still matters and the big type still explains it; the status chip
    * just has to be legible at a glance.
    */
  pill.textContent = !connected
    ? 'NOT CONNECTED'
    : screen === 'live' ? (hold || 'LIVE')
    : screen === 'slate' ? 'NOTHING LIVE'
    : screen === 'dark' ? 'NO SIGNAL'
    : 'CONNECTING';

  tile.hidden = !publishing;
  whyBtn.textContent = panelLabel();
  tileRelationEl.textContent = tileRelation(s, holdSeconds);

  air.muted = broadcastMuted(s, wantsSound);
  const blocked = soundBlockedReason(s);
  soundBtn.disabled = blocked !== null;
  soundBtn.title = blocked ?? '';
  soundBtn.setAttribute('aria-pressed', String(!air.muted));

  const p = primaryAction(s);
  primaryBtn.textContent = p.label;
  primaryBtn.dataset.kind = p.kind;

  // Rating is only meaningful when a picture is actually on this screen.
  const canRate = screen === 'live' && liveFeedId !== null;
  goodBtn.disabled = !canRate;
  badBtn.disabled = !canRate;

  if (publishing) {
    paintMine();
    // The reason, where the eye already is. Not a replacement for the three
    // artifacts in the panel: the first blocker, and a pointer to the rest.
    const blockers = myFeed === null ? [] : eligibility(myFeed, Date.now(), standard).blockers;
    noteEl.textContent = publisherHeadline(s, blockers, panelLabel());
  }
}

function paintMine(): void {
  const view = publisherView(
    myFeedId !== null && onAirId() === myFeedId,
    myFeed?.score?.passes === false || myFeed === null
      ? (myFeed === null ? [] : myFeed.score?.failures ?? [])
      : eligibility(myFeed, Date.now(), standard).blockers,
    wasOnAir,
    myFeedId !== null,
    connected,
  );
  verdictEl.className = view.className;
  verdictEl.textContent = view.text;
  const blockers = myFeed === null ? [] : eligibility(myFeed, Date.now(), standard).blockers;
  reasonsEl.replaceChildren(...blockers.map((r) => {
    const li = document.createElement('li');
    li.textContent = r;
    return li;
  }));
  cameraActiveEl.textContent = activeCameraLabel(cameras, chosenCamera, syntheticBox.checked);
}

// --- the sheet ----------------------------------------------------------------

function paintSheet(): void {
  const rows: string[] = [];
  if (onAir.kind === 'slate') {
    rows.push(slateCopy(onAir.reason).why);
  } else {
    const f = feeds.find((x) => x.id === onAirId());
    rows.push(f === undefined ? 'A stream is playing.' : `${f.angle}, streamed by ${f.operator}.`);
    rows.push(`Shown ${holdSeconds ?? '?'} seconds behind, so every check finishes before you see it.`);
  }
  rows.push(`${feeds.length} stream${feeds.length === 1 ? '' : 's'} on offer right now.`);
  // Wave 6 section 3. The EFFECTIVE location of whatever is on air: its own if
  // it set one, the session's otherwise. Hidden rather than blank when there is
  // none, which is LOC1.
  {
    const air = feeds.find((f) => f.id === onAirId()) ?? null;
    const label = locationLabel(effectiveLocation(air, sessionLocation));
    sheetLocation.textContent = label ?? '';
    sheetLocation.hidden = label === null;
  }
  sheetBroadcast.replaceChildren(...rows.map((r) => {
    const p = document.createElement('p');
    p.className = 'sheet-line';
    p.textContent = r;
    return p;
  }));
  sheetMine.hidden = !publishing;
}

/**
 * What the panel button says.
 *
 * "Open the sheet" shipped in two instructions on 2026-08-29 and was reported
 * as meaningless within the hour, correctly: "sheet" is a designer's word for a
 * panel that slides up, it appears nowhere on the screen, and the button it was
 * pointing at said something else entirely. An instruction that names a control
 * the reader cannot find is worse than no instruction.
 *
 * So the button is labelled by what is behind it, and every message that sends
 * somebody there quotes that exact label.
 */
function panelLabel(): string {
  return publishing ? 'Your stream' : 'Why this stream';
}

/** Where focus was before the sheet opened, so it can be given back. */
let focusBeforeSheet: HTMLElement | null = null;

/** Everything focusable inside the sheet, in document order. */
function sheetFocusables(): HTMLElement[] {
  return [...sheet.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )].filter((el) => el.offsetParent !== null);
}

function openSheet(open: boolean): void {
  const wasOpen = !sheet.hidden;
  sheet.hidden = !open;
  whyBtn.textContent = panelLabel();
  whyBtn.setAttribute('aria-expanded', String(open));
  document.body.dataset.sheet = open ? 'open' : 'closed';
  if (open) {
    paintSheet();
    // Remember where we came from BEFORE moving focus, or there is nothing to
    // return to and a keyboard lands back at the top of the document.
    if (!wasOpen) focusBeforeSheet = document.activeElement as HTMLElement | null;
    (sheetFocusables()[0] ?? sheetClose).focus();
  } else if (wasOpen) {
    // Give it back. A dialog that closes and drops focus leaves a person at the
    // start of the page, which is worse than never having moved it.
    focusBeforeSheet?.focus();
    focusBeforeSheet = null;
  }
}

// --- cameras ------------------------------------------------------------------

async function refreshCameras(): Promise<void> {
  let devices: MediaDeviceInfo[] = [];
  try {
    devices = await navigator.mediaDevices.enumerateDevices();
  } catch {
    devices = [];
  }
  cameras = devices.filter((d) => d.kind === 'videoinput').map((d) => ({ deviceId: d.deviceId, label: d.label }));
  chosenCamera = chooseCamera(cameras, chosenCamera);
  cameraSelect.replaceChildren();
  const auto = document.createElement('option');
  auto.value = '';
  auto.textContent = cameras.length === 0 ? 'No camera found' : 'Default camera';
  cameraSelect.append(auto);
  cameras.forEach((c, i) => {
    const o = document.createElement('option');
    o.value = c.deviceId;
    o.textContent = cameraName(c, i);
    cameraSelect.append(o);
  });
  cameraSelect.value = chosenCamera ?? '';
  cameraActiveEl.textContent = activeCameraLabel(cameras, chosenCamera, syntheticBox.checked);
}

function openCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: cameraConstraints(chosenCamera, prefersRearCamera(navigator.maxTouchPoints)),
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  });
}

// --- going live ---------------------------------------------------------------

async function start(): Promise<void> {
  primaryBtn.disabled = true;
  primaryBtn.textContent = 'Starting...';
  let stream: MediaStream;
  if (syntheticBox.checked) {
    synthetic = syntheticSource(angleInput.value || myPath);
    stream = synthetic.stream;
  } else {
    try {
      stream = await openCamera();
      await refreshCameras();
    } catch (err) {
      statusEl.textContent = `Camera refused: ${String(err)}.`;
      noteEl.textContent = `Camera refused. Tap "${panelLabel()}" below to choose a different one.`;
      chosenCamera = null;
      localStorage.removeItem('sb.camera');
      await refreshCameras();
      primaryBtn.disabled = false;
      paint();
      return;
    }
  }

  me.srcObject = stream;
  await me.play().catch(() => undefined);

  try {
    // The venue token, when the media plane is authenticating. Empty on a LAN,
    // where the publish door is open and the four gates are what make that safe.
    pc = await publish(withToken(whipUrl(mediaBase, myPath), mediaToken(location.search)), stream);
    sender = pc.getSenders().find((x) => x.track?.kind === 'video') ?? null;
    liveStream = stream;
    collector = new VitalsCollector({ pc, video: me, stream });
    await collector.start();
  } catch (err) {
    // Everything acquired before the failure is released here. Guard P8: a
    // start that failed after the camera opened used to leave the capture
    // running behind a button reading "Starting...", with the tally light on.
    synthetic?.stop();
    for (const track of stream.getTracks()) track.stop();
    me.srcObject = null;
    const shown = startFailure(err);
    statusEl.textContent = shown.status;
    noteEl.textContent = shown.status;
    primaryBtn.disabled = shown.buttonDisabled;
    paint();
    return;
  }

  publishing = true;
  primaryBtn.disabled = false;
  applyFieldLocks();
  wireAngleOnce();
  localStorage.setItem('sb.operator', operatorInput.value);
  localStorage.setItem('sb.angle', angleInput.value);
  bus?.send({
    type: 'register',
    path: myPath,
    angle: angleInput.value || myPath,
    operator: operatorInput.value || 'anonymous',
    // Wave 6 stage 2b. Omitted entirely when blank, so a publisher who says
    // nothing inherits the session's default rather than overriding it with an
    // empty one. The server normalises this and a blank name is null there too.
    ...(locationEl.value.trim() === '' ? {} : { location: { name: locationEl.value.trim() } }),
  });
  const c = collector;
  vitalsTimer = window.setInterval(() => {
    if (c !== null) bus?.send({ type: 'vitals', vitals: c.snapshot() });
  }, 1000);
  paint();
}

function stop(): void {
  if (stopNeedsConfirm(state())) {
    // The only confirmed action on this page, and it is at the BACK rather than
    // the front: you are the broadcast, and stopping may cut it to the slate.
    const ok = window.confirm('You are on air. Stopping ends the broadcast for everyone watching. Stop?');
    if (!ok) return;
  }
  clearInterval(vitalsTimer);
  collector?.stop();
  pc?.close();
  synthetic?.stop();
  for (const track of liveStream?.getTracks() ?? []) track.stop();
  me.srcObject = null;
  publishing = false;
  applyFieldLocks();
  wasOnAir = false;
  myFeedId = null;
  myFeed = null;
  sender = null;
  liveStream = null;
  synthetic = null;
  pinned = null;
  noteEl.textContent = '';
  paint();
}

/**
 * The slate's link to the standard, painted once when config arrives.
 *
 * Section 3 item 3: the slate is where a viewer is most likely to want the
 * rule, and until now it named a bar with nowhere to read it. Rendered as a
 * real anchor rather than as text, and absent entirely when there is nowhere to
 * point, because `standardLink` returns null rather than a dead href.
 */
function paintStandardLink(): void {
  const link = standardLink(standardUrl);
  const existing = document.getElementById('slate-standard');
  existing?.remove();
  if (link === null) return;
  const a = document.createElement('a');
  a.id = 'slate-standard';
  a.className = 'slate-standard';
  a.href = link.href;
  a.textContent = link.label;
  a.rel = 'noopener';
  slateReason.after(a);
}

// --- feedback -----------------------------------------------------------------

function renderFeedback(fb: Feedback): void {
  feedbackEl.hidden = false;
  const gates = Object.entries(fb.ruling.perGate);
  rulingEl.replaceChildren(...[
    Object.assign(document.createElement('h3'), { textContent: 'The ruling' }),
    ...gates.map(([gate, r]) => {
      const li = document.createElement('div');
      li.className = r.passed ? 'gate-ok' : 'gate-no';
      li.textContent = `${gate}: ${r.passed ? 'held' : r.reason?.rubricLine ?? 'refused'}`;
      return li;
    }),
  ]);
  /*
   * The appeal route, section 5. A ruling a person cannot argue with is a
   * ruling they can only comply with, and the address rides on the reason
   * rather than on this page, so a ruling stays appealable under the terms in
   * force when it was made.
   */
  const reasons = Object.values(fb.ruling.perGate)
    .map((g) => g.reason)
    .filter((r): r is NonNullable<typeof r> => r !== null);
  const appeal = appealLine(reasons, fb.feedId, Date.now());
  if (appeal !== null) {
    rulingEl.append(Object.assign(document.createElement('p'), {
      className: 'appeal',
      id: 'fb-appeal',
      textContent: appeal,
    }));
  }
  const link = standardLink(standardUrl);
  if (link !== null) {
    const a = document.createElement('a');
    a.id = 'fb-standard';
    a.className = 'ruling-standard';
    a.href = link.href;
    a.rel = 'noopener';
    a.textContent = link.label;
    rulingEl.append(a);
  }
  /*
   * §5 and §7: the per-channel answer, which went to the wire for a stage
   * before anything rendered it. A publisher on two channels has two fates and
   * may be selected on one and refused on the other, and a publisher refused
   * MEMBERSHIP has neither: they asked, the board answered "not you", and until
   * this line existed that answer was never shown to them.
   */
  const perChannel = channelLines(fb);
  fateEl.replaceChildren(
    Object.assign(document.createElement('h3'), { textContent: 'What happened to your feed' }),
    Object.assign(document.createElement('p'), {
      className: 'fate-line',
      textContent: fateContext(fb.fate),
    }),
    ...perChannel.map((c) => Object.assign(document.createElement('p'), {
      // Two classes, because the two claims are different and a stylesheet that
      // could not tell them apart would render a membership refusal as a loss.
      className: c.weighed ? 'fate-channel' : 'fate-channel fate-declined',
      textContent: `#${c.channelId}: ${c.line}`,
    })),
  );
  coachingEl.replaceChildren(
    Object.assign(document.createElement('h3'), { textContent: 'Coaching' }),
    Object.assign(document.createElement('p'), {
      className: 'coach-quiet',
      textContent: fb.coaching?.note ?? 'Nothing to suggest right now.',
    }),
  );
}

// --- boot ---------------------------------------------------------------------

async function main(): Promise<void> {
  mountNav();
  const cfg = await loadConfig();
  hlsBase = cfg.hlsBase;
  mediaBase = cfg.mediaBase;
  standardUrl = cfg.standardUrl ?? '';
  paintStandardLink();
  holdSeconds = Math.round(cfg.delayMs / 1000);
  myPath = localStorage.getItem('sb.path') ?? `feed-${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem('sb.path', myPath);
  operatorInput.value = localStorage.getItem('sb.operator') ?? '';
  angleInput.value = localStorage.getItem('sb.angle') ?? '';

  // Safe on load: enumerating does not prompt. `getUserMedia` still runs only
  // on the tap, which is call 2's rule 1.
  await refreshCameras();
  navigator.mediaDevices.addEventListener?.('devicechange', () => void refreshCameras());

  showSlate('connecting');
  setInterval(paint, 1000);

  bus = new Bus(
    wsUrl(),
    (msg) => {
      if (msg.type === 'state') {
        feeds = msg.feeds;
        onAir = msg.onAir;
        standard = msg.standard;
        sessionLocation = msg.location;
        // Tell a publisher what they would INHERIT, next to the field where
        // they decide whether to override it. Without this the placeholder
        // referred to "the event" and never said what it was.
        applyFieldLocks();
        myFeed = msg.feeds.find((f) => f.id === myFeedId) as Feed | undefined ?? null;
        if (myFeedId !== null && onAirId() === myFeedId) wasOnAir = true;
        applyState();
        if (!sheet.hidden) paintSheet();
      } else if (msg.type === 'ack') {
        myFeedId = msg.feedId;
        /*
         * A registration is a partial success when a declared channel is not
         * carried here, and it used to be reported as a plain success. The note
         * line is the right place because this is about what the publisher
         * ASKED for at registration, not about a window: `renderFeedback` runs
         * once a window and may not run for a while, or ever.
         */
        const dropped = msg.declined ?? [];
        if (dropped.length > 0) noteEl.textContent = dropped.map((d) => d.reason).join(' ');
      } else if (msg.type === 'feedback') {
        renderFeedback(msg.payload);
      } else if (msg.type === 'refused') {
        noteEl.textContent = `Refused: ${msg.reason}`;
        statusEl.textContent = `Refused: ${msg.reason}`;
      }
      paint();
    },
    (up) => {
      connected = up;
      paint();
    },
  );

  primaryBtn.addEventListener('click', () => {
    if (state() === 'watching') void start();
    else stop();
  });

  // Tap the small tile to swap, and the choice STICKS for the session.
  const doSwap = (): void => {
    pinned = mainView(state(), pinned) === 'broadcast' ? 'camera' : 'broadcast';
    paint();
  };
  swapBtn.addEventListener('click', (e) => { e.stopPropagation(); doSwap(); });
  tile.addEventListener('click', doSwap);

  soundBtn.addEventListener('click', () => {
    if (soundBlockedReason(state()) !== null) return;
    wantsSound = !wantsSound;
    paint();
    if (!air.muted) void air.play().catch(() => undefined);
  });

  goodBtn.addEventListener('click', () => rate(false));
  badBtn.addEventListener('click', () => rate(true));
  whyBtn.addEventListener('click', () => openSheet(sheet.hidden));

  /**
   * Escape closes, and Tab stays inside.
   *
   * On the document rather than the sheet, because focus can be anywhere when
   * the key is pressed and a listener on the sheet only fires for focus that is
   * already in it, which is the case that needs no help.
   */
  document.addEventListener('keydown', (ev) => {
    if (closesDialog(ev.key, !sheet.hidden)) {
      ev.preventDefault();
      openSheet(false);
      return;
    }
    if (ev.key !== 'Tab' || sheet.hidden) return;
    const focusables = sheetFocusables();
    const to = trapIndex(focusables.indexOf(document.activeElement as HTMLElement),
      focusables.length, ev.shiftKey);
    if (to === null) return;
    ev.preventDefault();
    focusables[to]?.focus();
  });
  sheetClose.addEventListener('click', () => openSheet(false));
  syntheticBox.addEventListener('change', () => {
    cameraActiveEl.textContent = activeCameraLabel(cameras, chosenCamera, syntheticBox.checked);
  });

  cameraSelect.addEventListener('change', async () => {
    chosenCamera = cameraSelect.value === '' ? null : cameraSelect.value;
    if (chosenCamera === null) localStorage.removeItem('sb.camera');
    else localStorage.setItem('sb.camera', chosenCamera);
    cameraActiveEl.textContent = activeCameraLabel(cameras, chosenCamera, syntheticBox.checked);
    if (sender === null || liveStream === null) return;
    let next: MediaStream;
    try {
      next = await openCamera();
    } catch (err) {
      statusEl.textContent = `Could not switch camera: ${String(err)}. Still streaming the previous one.`;
      return;
    }
    const track = next.getVideoTracks()[0];
    if (track === undefined) return;
    const old = sender.track;
    await sender.replaceTrack(track);
    old?.stop();
    if (old !== null) liveStream.removeTrack(old);
    liveStream.addTrack(track);
    me.srcObject = liveStream;
  });

  paint();
}

function rate(bad: boolean): void {
  if (liveFeedId === null) return;
  bus?.send({ type: 'rate', feedId: liveFeedId, bad });
  goodBtn.classList.toggle('chosen', !bad);
  badBtn.classList.toggle('chosen', bad);
  noteEl.textContent = 'Noted. Ratings are weighed over time, and never change what is on screen now.';
}

void main();
